import shotFrontUrl from './assets/landing_test_pic/test_01.webp';
import shotBackUrl  from './assets/landing_test_pic/test_02.webp';

type ShotSlot = 'primary' | 'secondary';
type LinkSlot = 'download' | 'disclaimer';
type Shot = { id: string; slot: ShotSlot; fallbackSrc: string; alt: string };
type ScreenshotOverrides = Partial<Record<ShotSlot, string>>;
type LinkOverrides = Partial<Record<LinkSlot, string>>;
type TextOverrides = {
  heroTitle?: string;
  heroSubtitle?: string;
  heroBadgeTexts?: string[];
  downloadMetaTexts?: string[];
};
type LandingConfig = {
  screenshots: ScreenshotOverrides;
  links: LinkOverrides;
  text: TextOverrides;
};

const SHOTS: Shot[] = [
  { id: 'shotFront', slot: 'primary', fallbackSrc: shotFrontUrl, alt: '桌面端截图 1 — 题库主界面' },
  { id: 'shotBack',  slot: 'secondary', fallbackSrc: shotBackUrl,  alt: '桌面端截图 2 — 做题工作台' },
];

const CONFIG_URL = '/api/landing/v1/config';
const FETCH_TIMEOUT_MS = 3500;
const LOCAL_HTTP_HOSTS = new Set(['localhost', '127.0.0.1', '[::1]', '::1']);

// Negative offset = back's left edge sits behind front's right edge by 28px,
// so the back shot peeks out by a constant ribbon regardless of front's width.
function layoutBack(): void {
  const front = document.getElementById('shotFront');
  const back  = document.getElementById('shotBack');
  if (!front || !back) return;
  const w = front.getBoundingClientRect().width;
  if (w > 0) back.style.left = (w - 28) + 'px';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function unwrapConfigPayload(payload: unknown): Record<string, unknown> | null {
  if (!isRecord(payload)) return null;
  if (isRecord(payload.result)) return payload.result;
  if (isRecord(payload.data)) return payload.data;
  return payload;
}

function normalizeScreenshotUrl(value: unknown): string {
  return normalizeConfigUrl(value);
}

function normalizeLinkUrl(value: unknown): string {
  return normalizeConfigUrl(value);
}

function normalizeText(value: unknown, maxLength: number): string {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!text || text.length > maxLength) return '';
  return text;
}

function normalizeTextArray(value: unknown, maxItems: number, maxLength: number): string[] | undefined {
  if (!Array.isArray(value) || value.length > maxItems) return undefined;
  const items: string[] = [];
  for (const item of value) {
    const text = normalizeText(item, maxLength);
    if (text) items.push(text);
  }
  return items;
}

function normalizeConfigUrl(value: unknown): string {
  if (typeof value !== 'string') return '';
  const text = value.trim();
  if (!text) return '';

  try {
    const url = new URL(text, window.location.href);
    const localHttp = url.protocol === 'http:' && LOCAL_HTTP_HOSTS.has(url.hostname);
    const sameOrigin = url.origin === window.location.origin && ['http:', 'https:'].includes(url.protocol);
    if (url.protocol === 'https:' || localHttp || sameOrigin) return url.href;
  } catch {
    // Invalid configured image URLs fall back to the bundled WebP slot image.
  }
  return '';
}

function readLandingConfig(payload: unknown): LandingConfig {
  const source = unwrapConfigPayload(payload);
  if (!source) return { screenshots: {}, links: {}, text: {} };
  return {
    screenshots: {
      primary: normalizeScreenshotUrl(source.screenshotPrimaryUrl),
      secondary: normalizeScreenshotUrl(source.screenshotSecondaryUrl),
    },
    links: {
      download: normalizeLinkUrl(source.downloadUpdatesUrl),
      disclaimer: normalizeLinkUrl(source.disclaimerUrl),
    },
    text: {
      heroTitle: normalizeText(source.heroTitle, 120) || undefined,
      heroSubtitle: normalizeText(source.heroSubtitle, 300) || undefined,
      heroBadgeTexts: normalizeTextArray(source.heroBadgeTexts, 5, 24),
      downloadMetaTexts: normalizeTextArray(source.downloadMetaTexts, 3, 24),
    },
  };
}

const FADE_MS = 550;
let activeRender = 0;

function renderShot(shot: Shot, src: string): Promise<HTMLElement | null> {
  return new Promise((resolve) => {
    const el = document.getElementById(shot.id);
    if (!el) {
      resolve(null);
      return;
    }

    const fallbackUrl = new URL(shot.fallbackSrc, window.location.href).href;
    const img = document.createElement('img');
    img.alt = shot.alt;
    img.loading = 'lazy';
    img.decoding = 'async';
    img.dataset.slot = shot.slot;
    img.dataset.source = src === shot.fallbackSrc ? 'fallback' : 'config';
    img.dataset.targetSrc = src;

    const apply = (): void => {
      if (img.naturalWidth) {
        el.style.aspectRatio = (img.naturalWidth / img.naturalHeight).toFixed(4);
      }
      layoutBack();
      resolve(el);
    };

    img.addEventListener('load', apply, { once: true });
    img.addEventListener('error', () => {
      if (img.src === fallbackUrl) {
        apply();
        return;
      }
      img.dataset.source = 'fallback';
      img.src = shot.fallbackSrc;
    });

    img.src = src;
    el.replaceChildren(img);
  });
}

// Shimmer + staggered fade-in:
//   1. Detect which shots actually need re-rendering. A repeat call with the
//      same target src (e.g. config fetch returned empty) bails out — we don't
//      re-flash a preview that's already settled.
//   2. Mark `.preview-frame` as `.is-loading` so the shimmer overlay runs.
//   3. Wait for all images to load (or error) AND for at least `minHold` ms —
//      ensures the shimmer reads even when images come from disk cache.
//   4. Drop `.is-loading`, then add `.is-loaded` to front immediately and to
//      back FADE_MS later so back's fade starts when front's finishes.
//   `activeRender` token guards against overlapping calls (e.g. fast config
//   fetch that arrives before the initial render's hold finishes).
function renderShots(overrides: ScreenshotOverrides = {}, opts: { minHold?: number } = {}): void {
  const myRender = ++activeRender;
  const minHold = opts.minHold ?? 650;
  const frame = document.querySelector<HTMLElement>('.preview-frame');

  const tasks: Array<Promise<HTMLElement | null>> = [];
  let needsAnimation = false;

  for (const s of SHOTS) {
    const target = overrides[s.slot] || s.fallbackSrc;
    const el = document.getElementById(s.id);
    if (!el) {
      tasks.push(Promise.resolve(null));
      continue;
    }
    const existing = el.querySelector<HTMLImageElement>('img');
    if (
      existing &&
      existing.dataset.targetSrc === target &&
      existing.complete &&
      existing.naturalWidth > 0
    ) {
      tasks.push(Promise.resolve(null));
      continue;
    }
    needsAnimation = true;
    el.classList.remove('is-loaded');
    tasks.push(renderShot(s, target));
  }

  if (!needsAnimation) return;

  frame?.classList.add('is-loading');
  const start = performance.now();

  void Promise.all(tasks).then((els) => {
    if (myRender !== activeRender) return;
    const elapsed = performance.now() - start;
    const wait = Math.max(0, minHold - elapsed);
    setTimeout(() => {
      if (myRender !== activeRender) return;
      layoutBack();
      frame?.classList.remove('is-loading');
      els.forEach((el, i) => {
        if (!el) return;
        setTimeout(() => el.classList.add('is-loaded'), i * FADE_MS);
      });
    }, wait);
  });
}
renderShots();
window.addEventListener('resize', layoutBack);

function applyConfigLinks(links: LinkOverrides = {}): void {
  (Object.keys(links) as LinkSlot[]).forEach((slot) => {
    const href = links[slot];
    if (!href) return;

    document.querySelectorAll<HTMLAnchorElement>(`a[data-config-link="${slot}"]`).forEach((anchor) => {
      anchor.href = href;

      const url = new URL(href, window.location.href);
      const external = url.origin !== window.location.origin;
      if (external) {
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
      } else {
        anchor.removeAttribute('target');
        anchor.removeAttribute('rel');
      }
    });
  });
}

function splitHeroTitleLines(title: string): string[] {
  const lines = title
    .replace(/\\n/g, '\n')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.length ? lines : [title];
}

function renderHeroTitle(title: string): void {
  const el = document.querySelector<HTMLElement>('h1.title');
  if (!el) return;

  el.replaceChildren();
  for (const line of splitHeroTitleLines(title)) {
    const span = document.createElement('span');
    span.className = 'line';
    span.textContent = line;
    el.append(span);
  }
}

function renderHeroBadges(texts: string[]): void {
  const container = document.querySelector<HTMLElement>('[data-config-badges]');
  if (!container) return;

  container.replaceChildren();
  container.hidden = texts.length === 0;

  for (const text of texts) {
    const badge = document.createElement('span');
    badge.className = 'eyebrow';

    const dot = document.createElement('span');
    dot.className = 'dot';
    dot.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.textContent = text;

    badge.append(dot, label);
    container.append(badge);
  }
}

function renderDownloadMeta(texts: string[]): void {
  const container = document.querySelector<HTMLElement>('[data-config-meta]');
  if (!container) return;

  container.replaceChildren();
  container.hidden = texts.length === 0;

  texts.forEach((text, index) => {
    if (index > 0) {
      const sep = document.createElement('span');
      sep.className = 'sep';
      sep.textContent = '·';
      container.append(sep);
    }

    const item = document.createElement('span');
    item.textContent = text;
    container.append(item);
  });
}

function applyConfigText(text: TextOverrides = {}): void {
  if (text.heroTitle) renderHeroTitle(text.heroTitle);

  const subtitle = document.querySelector<HTMLElement>('p.subtitle');
  if (subtitle && text.heroSubtitle) subtitle.textContent = text.heroSubtitle;

  if (text.heroBadgeTexts) renderHeroBadges(text.heroBadgeTexts);
  if (text.downloadMetaTexts) renderDownloadMeta(text.downloadMetaTexts);
}

function refreshFrameSoon(): void {
  window.requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
}

async function fetchLandingConfig(): Promise<LandingConfig> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(CONFIG_URL, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) return { screenshots: {}, links: {}, text: {} };
    return readLandingConfig(await response.json() as unknown);
  } catch {
    return { screenshots: {}, links: {}, text: {} };
  } finally {
    window.clearTimeout(timeout);
  }
}

void fetchLandingConfig().then((config) => {
  renderShots(config.screenshots);
  applyConfigLinks(config.links);
  applyConfigText(config.text);
  document.documentElement.dataset.landingScreenshotConfig = Object.values(config.screenshots).some(Boolean)
    ? 'configured'
    : 'fallback';
  document.documentElement.dataset.landingLinkConfig = Object.values(config.links).some(Boolean)
    ? 'configured'
    : 'default';
  document.documentElement.dataset.landingTextConfig = Object.values(config.text).some(Boolean)
    ? 'configured'
    : 'default';
  refreshFrameSoon();
});

// ----- theme toggle -----
const root = document.documentElement;
const themeBtn = document.getElementById('themeToggle');
themeBtn?.addEventListener('click', () => {
  const next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
  root.setAttribute('data-theme', next);
  try {
    localStorage.setItem('xxgg-theme', next);
  } catch {
    // storage may be disabled
  }
});

// ----- content-frame line positioning -----
// Horizontal lines sit halfway between the chrome border (header/footer) and the
// first/last content blocks. We recompute on resize, font-load and title style
// mutations because title height shifts when fonts swap in.
(function positionFrame() {
  const main  = document.querySelector<HTMLElement>('main');
  const frame = document.querySelector<HTMLElement>('.content-frame');
  const head  = document.querySelector<HTMLElement>('.hero-head');
  const tail  = document.querySelector<HTMLElement>('.meta')
             ?? document.querySelector<HTMLElement>('.downloads');
  const title = document.querySelector<HTMLElement>('h1.title');
  if (!main || !frame || !head || !tail) return;

  function update(): void {
    if (!main || !frame || !head || !tail) return;
    const mb = main.getBoundingClientRect();
    const hb = head.getBoundingClientRect();
    const tb = tail.getBoundingClientRect();
    const topGap    = Math.max(0, hb.top    - mb.top);
    const bottomGap = Math.max(0, mb.bottom - tb.bottom);
    frame.style.setProperty('--frame-inset-top',    Math.round(topGap    / 2) + 'px');
    frame.style.setProperty('--frame-inset-bottom', Math.round(bottomGap / 2) + 'px');
  }

  update();
  window.addEventListener('resize', update);
  if (document.fonts?.ready) document.fonts.ready.then(update);
  if (title) {
    new MutationObserver(update).observe(title, { attributes: true, attributeFilter: ['style'] });
  }
})();
